import React, {FC, useEffect, useState} from "react"

import withBranding, {WithBranding} from "@irati/brand/withBranding"
import authentication from "@irati/auth"
import {Deal as IDeal, DealType} from "@irati/types"
import {getColorFromThumbnail, getDealTileImage, getDealTileImageWithPadding} from "@irati/utils/images"
import id from "@irati/utils/id"
import tracking from "@irati-tracking/perks/perks"
import {determineDealThumbnailImage, determineDealColor} from "@irati/deals/helpers/deals/appearance"
import isPerksGO from "@irati/deals/helpers/deals/isPerksGO"
import withPerkOrReward, {WithPerkOrReward} from "@irati-web/flexi-rewards/src/utils/withPerkOrReward"

import {GradientOverlay} from "@irati/ui/src/GradientOverlay/GradientOverlay"
import DealModes from "../../enums/DealModes"
import DealPages from "../../enums/DealPages"
import PillsOverlay from "./PillsOverlay"
import DEAL_PAGES from "../../constants/dealPage"
import {StyledDeal} from "./styled/Deal"
import CardImageWrapper from "./styled/CardImageWrapper"
import DealAvatar from "./styled/DealAvatar"
import CardImage from "./styled/CardImage"
import FavouriteButton from "./styled/FavouriteButton"
import {FavouriteButtonOnChange} from "../Favourite/IconButton"
import dealDefaultCardImage from "../../images/dealDefaultCardImage.jpg"
import DealBody from "./DealBody"
import {shouldDisplayDealAvatar} from "../../utils/deals/imageUtils"
import DealTileOnClick from "./DealTileOnClick"

interface Props extends WithBranding, WithPerkOrReward {
  deal: IDeal,
  trackingData: {
    position: number,
    sectionHeader: string
  },
  mode?: DealModes,
  page: DealPages,
  isFavouriteDeal: boolean,
  className?: string,
  useWalletDealCard?: boolean
}

const Deal: FC<Props> = (props) => {
  const {deal, mode = DealModes.Default, page, trackingData, isFavouriteDeal, className, branding, useWalletDealCard} = props
  const {businessLogoUrl: tenantImageLink, primaryColour: tenantColor} = branding
  const [dealColor, setDealColor] = useState<string>()
  const isTypeMerchandise = deal.type === DealType.Merchandise

  useEffect(() => {
    if (!deal.cardImage && props.deal.thumbnailImage) {
      getColorFromThumbnail(props.deal.thumbnailImage).then((color) => {
        setDealColor(color)
      })
    }
  }, [])

  const isPerksGoDeal = authentication.apps("perks.perksGo") && isPerksGO(deal)
  const idNamespace = id("deal", props)
  const compactMode = mode === DealModes.Compact
  const thumbnailImage = shouldDisplayDealAvatar(deal) ? determineDealThumbnailImage(deal, branding) : null
  const borderBottomColor = determineDealColor(deal, tenantImageLink, tenantColor)

  const onClickDeal = () => {
    switch (page) {
      case DealPages.SEARCH:
        tracking.clickSearchResult(deal)
        break
      case DealPages.HOME:
        tracking.clickHomepagePerk(deal, trackingData)
        break
      default:
        break
    }
  }

  const handleFavChange: FavouriteButtonOnChange = (isFavourite) =>
    isFavourite ? tracking.favouritePerk(deal, DEAL_PAGES.PERK) : tracking.unfavouritePerk(deal, DEAL_PAGES.PERK)
  const gradientType = isTypeMerchandise ? "linear" : "radial"
  let dealCardImageSrc = !deal.cardImage && !deal.thumbnailImage && dealDefaultCardImage
  if (deal.cardImage) {
    const imgTransformation = isTypeMerchandise ? getDealTileImageWithPadding : getDealTileImage
    dealCardImageSrc = imgTransformation((deal.cardImage))
  }

  return (
    <StyledDeal id={idNamespace} className={className}>
      <DealTileOnClick id={idNamespace} deal={deal} onClick={onClickDeal}>
        <CardImageWrapper>
          <CardImage id={id("img-banner", idNamespace)} image={dealCardImageSrc} color={dealColor} compactMode={compactMode} />
          <GradientOverlay id={id("banner-overlay", idNamespace)} gradientType={gradientType} />
          {isPerksGoDeal && <PillsOverlay deal={deal} isFavouriteDeal={isFavouriteDeal} />}
          {thumbnailImage && (
            <DealAvatar id={id("img-thumb", idNamespace)} compactMode={compactMode} src={thumbnailImage} small withShadow />
          )}
          <FavouriteButton deal={deal} onChange={handleFavChange} />
        </CardImageWrapper>
        <DealBody
          id={id("deal-body", idNamespace)}
          deal={deal}
          mode={mode}
          isFavouriteDeal={isFavouriteDeal}
          useWalletDealCard={useWalletDealCard}
          borderBottomColor={borderBottomColor} />
      </DealTileOnClick>
    </StyledDeal>
  )
}

export default withPerkOrReward(withBranding(Deal))
